import { useAuth0 } from '@auth0/auth0-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getProductOnboardingState,
  type ProductOnboardingState,
  updateProductOnboardingState,
} from '../services/user.service';
import { useDemoMode } from '../components/providers/DemoModeContext';

const DEFAULT_STATE: ProductOnboardingState = {
  intro_seen: false,
  dashboard_tour_done: false,
  job_details_tour_done: false,
};

const KEY_TO_FIELD: Record<string, keyof ProductOnboardingState> = {
  intro_seen: 'intro_seen',
  dashboard_tour_done: 'dashboard_tour_done',
  job_details_tour_done: 'job_details_tour_done',
};

export const useProductOnboarding = () => {
  const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();
  const { endDemo } = useDemoMode();
  const [state, setState] = useState<ProductOnboardingState>(DEFAULT_STATE);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;

    const load = async () => {
      if (isLoading) return;
      if (!isAuthenticated) {
        if (active) {
          setState(DEFAULT_STATE);
          setReady(true);
        }
        return;
      }

      try {
        const token = await getAccessTokenSilently();
        const response = await getProductOnboardingState(token);
        if (!active) return;
        setState(response.data ?? DEFAULT_STATE);
      } catch {
        if (active) setState(DEFAULT_STATE);
      } finally {
        if (active) setReady(true);
      }
    };

    setReady(false);
    load();
    return () => {
      active = false;
    };
  }, [getAccessTokenSilently, isAuthenticated, isLoading]);

  const getFlag = useCallback(
    (key: string): boolean => {
      const field = KEY_TO_FIELD[key];
      return field ? Boolean(state[field]) : false;
    },
    [state],
  );

  const setFlag = useCallback(
    async (key: string, value: boolean): Promise<void> => {
      const field = KEY_TO_FIELD[key];
      if (!field) return;

      setState((prev) => ({ ...prev, [field]: value }));

      if (!isAuthenticated) return;
      try {
        const token = await getAccessTokenSilently();
        await updateProductOnboardingState(token, { [field]: value });
      } catch {
        // Optimistic UI; next fetch reconciles.
      }
    },
    [getAccessTokenSilently, isAuthenticated],
  );

  const clearFlags = useCallback(
    async (keys: string[]): Promise<void> => {
      const patch: Partial<ProductOnboardingState> = {};
      keys.forEach((key) => {
        const field = KEY_TO_FIELD[key];
        if (field) patch[field] = false;
      });

      if (Object.keys(patch).length === 0) return;

      setState((prev) => ({ ...prev, ...patch }));

      if (!isAuthenticated) return;
      try {
        const token = await getAccessTokenSilently();
        await updateProductOnboardingState(token, patch);
      } catch {
        // Optimistic UI; next fetch reconciles.
      }
    },
    [getAccessTokenSilently, isAuthenticated],
  );

  const endDemoTour = useCallback(() => {
    endDemo();
  }, [endDemo]);

  useEffect(() => {
    const handleBeforeUnload = () => {
      endDemo();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [endDemo]);

  return useMemo(
    () => ({
      getFlag,
      setFlag,
      clearFlags,
      endDemoTour,
      ready,
    }),
    [clearFlags, endDemoTour, getFlag, ready, setFlag],
  );
};
